import React, { useState } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions } from "@material-ui/core";
import { useLibrary, useLibraryAlert } from "../../LibraryPage";
import { FolderService } from "../../../../services/FolderService";
import { convertFolderToLibraryItem } from "../../../../entities/Folder";
import { FieldValidationError, ValidationHelper } from "../../../../services/server/ServerResponses";
import { useHistory } from "react-router-dom";
import { routes } from "../../../../routes";
import { SermonSeriesService } from "../../../../services/SermonSeriesService";
import { convertSeriesToLibraryItem } from "../../../../entities/SermonSeries";

interface Props {
	selectedFolderType: "folder" | "series";
	onClose: () => void;
}

export function CreateFolderDialog(props: Props) {
	const context = useLibrary();
    const libraryAlert = useLibraryAlert();
	const history = useHistory();

	const { activeFolderId } = context;
	const [title, setTitle] = useState("");
	const [errors, setErrors] = useState<FieldValidationError[]>([]);
	const [disable, setDisable] = useState(false);

	const onCreate = (e:React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		props.selectedFolderType === "folder" 
			? onCreateFolder() 
			: onCreateSermonSeries()
	}

	const onCreateFolder = async () => {
        setDisable(true);
		const response = await FolderService.createFolder(title, activeFolderId);
        setDisable(false);

		if(response.error) return libraryAlert(response.message);
		if(response.validation) return setErrors(response.errors);
		
		const folder = response.data;
		context.addLibraryItem(convertFolderToLibraryItem(folder));
		libraryAlert(`Folder ${title} created`, async () => {
			const result = await FolderService.deleteFolder(folder.id);
			if(result.success) {
				context.removeLibraryItem(folder.id);
				libraryAlert(`Removed folder ${folder.name}`);
			} else {
				libraryAlert(`Failed to delete folder ${folder.name}`);
			}
		});
		history.push(routes.app.resolve.libraryFolder(folder.id), { prevUrl: window.location.href });
		props.onClose();
	};

	const onCreateSermonSeries = async () => {
        setDisable(true);
		const response = await SermonSeriesService.createSermonSeries(title, activeFolderId);
        setDisable(false);

		if(response.error) return libraryAlert(response.message);
		if(response.validation) return setErrors(response.errors);

		const series = response.data;
		context.addLibraryItem(convertSeriesToLibraryItem(series));
		libraryAlert(`Sermon Series ${title} created`, async () => {
			const result = await FolderService.deleteFolder(series.id);
			if(result.success) {
				context.removeLibraryItem(series.id);
				libraryAlert(`Removed Sermon Series ${series.name}`);
			} else {
				libraryAlert(`Failed to delete Sermon Series ${series.name}`);
			}
		});
		history.push(routes.app.resolve.libraryFolder(series.id), { prevUrl: window.location.href });
		props.onClose();
	};

	return (
		<Dialog open={true} onClose={props.onClose} aria-labelledby="form-dialog-title">
			<DialogTitle id="form-dialog-title">New {props.selectedFolderType}</DialogTitle>
			<form onSubmit={onCreate}>
				<DialogContent>
					<DialogContentText>Enter the name of your new {props.selectedFolderType}.</DialogContentText>
					<TextField
						required
						margin="dense"
						id="newItemName"
						label="Name"
						type="text"
						onChange={(e) => setTitle(e.currentTarget.value)}
						fullWidth
						value={title}
						error={ValidationHelper.isFieldInError(errors, "Name")}
						helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, "Name") }} />}
						/>
				</DialogContent>
				<DialogActions>
					<Button onClick={props.onClose} color="primary">
						Cancel
					</Button>
					<Button color="primary" disabled={disable} type="submit">
						Add
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
